import { Item, Partition, Setter, Walls } from '@types'
import { quatYToDeg } from '@utils/conversion/quatYToDeg'
import toQuaternion from '@utils/conversion/toQuaternion'
import { toVector3 } from '@utils/conversion/toVector3'
import decomposeMatrix from '@utils/decomposeMatrix'
import { radians } from '@utils/generalMaths/geometry'
import isPartition from '@utils/sceneObjects/assertions/isPartition'
import getId from '@utils/sceneObjects/getId'
import { addItemOffset } from '@utils/sceneObjects/items/addItemOffset'
import { getBasePartition } from '@utils/sceneObjects/partitions/getBasePartition'
import { Euler, Matrix3, Matrix4, Quaternion } from 'three'
import { OBB } from 'three/examples/jsm/math/OBB'

export const createPartitionsActions = (set: Setter) => ({
  drawPartition: (heights?: number) => {
    set((store) => {
      const height = !heights ? store.prefs.ALTOPARED : heights
      const width = 1500
      const thickness = 100
      const partitionID = getId('partition')
      const position = toVector3([0, height / 2, 0])

      const newMatrix = new Matrix4()
      newMatrix.makeRotationY(radians(-180)).setPosition(position)

      store.items.set(
        partitionID,
        getBasePartition(
          {
            showAngle: false,
            visible: true,
            thickness: thickness,
            rotation: 0,
            x: 0,
            z: 0,
            id: partitionID,
            height,
            width: width,
            matrix: newMatrix,
          },
          store,
        ),
      )
      store.layers[store.currentLayer].items.add(partitionID)
      store.cameraCenter = [0, 4000, 0]
      store.currentObjectID = partitionID
      store.select(partitionID, false)
    })
  },
  editPartitionProperty: <T extends Partition>(
    id: string,
    config?: Partial<Record<keyof T, T[keyof T]>>,
  ) => {
    set((store) => {
      const item = store.items.get(id)
      if (!config) return
      for (const [property, value] of Object.entries(config)) {
        if (value instanceof Matrix4) {
          item[property] = value.clone()
          continue
        }
        item[property] = value
      }
    })
  },
  editPartitionAngle: (id: string, angle: number) => {
    set((store) => {
      const partition = store.items.get(id) as Partition
      const position = toVector3()

      partition.matrix.decompose(position, new Quaternion(), toVector3())
      partition.matrix.makeRotationY(radians(-angle))
      partition.matrix.setPosition(position)
    })
  },
  editPartitionAngleX: (id: string, angle: number) => {
    set((store) => {
      const partition = store.items.get(id) as Partition
      const position = toVector3()
      if (!isPartition(partition)) return
      partition.matrix.decompose(position, new Quaternion(), toVector3())
      partition.matrix.makeRotationX(radians(-angle))
      partition.matrix.setPosition(position)
    })
  },
  editPartitionAngleZ: (id: string, angle: number) => {
    set((store) => {
      const partition = store.items.get(id) as Partition
      const position = toVector3()
      if (!isPartition(partition)) return
      partition.matrix.decompose(position, new Quaternion(), toVector3())
      partition.matrix.makeRotationZ(radians(-angle))
      partition.matrix.setPosition(position)
    })
  },
  drawPartitionFromWall: (id: string) => {
    set((store) => {
      const wall = store.items.get(id) as Walls | Partition
      const height = wall.height
      const width = 1000
      const thickness = 100
      const partitionID = getId('partition')
      const position = toVector3()

      const rotation = wall.rotation
      const x =
        wall.x -
        ((width / 2 + wall.thickness / 2) * Math.sin(rotation) +
          (wall.thickness / 2) * Math.cos(rotation))
      const z =
        wall.z +
        ((width / 2 + wall.thickness / 2) * Math.cos(rotation) -
          (wall.thickness / 2) * Math.sin(rotation))

      const newMatrix = new Matrix4().compose(
        toVector3([x, height / 2, z]),
        toQuaternion().setFromEuler(
          new Euler().setFromVector3(toVector3([0, getRightPerpendicular(-rotation), 0])),
        ),
        toVector3([1, 1, 1]),
      )
      newMatrix.decompose(position, toQuaternion(), toVector3())
      function getRightPerpendicular(angle: number) {
        const perpendicularAngle = angle - Math.PI / 2
        return perpendicularAngle
      }
      store.items.set(
        partitionID,
        getBasePartition(
          {
            showAngle: false,
            visible: true,
            thickness: thickness,
            rotation: 0,
            x: x + (width / 2) * -Math.sin(rotation),
            z: z + (thickness / 2) * Math.cos(rotation),
            id: partitionID,
            height,
            width: width,
            matrix: newMatrix,
          },
          store,
        ),
      )
      store.layers[store.currentLayer].items.add(partitionID)
      store.currentObjectID = partitionID
    })
  },
  drawPartitionFromPartition: (id: string) => {
    set((store) => {
      const wall = store.items.get(id) as Partition
      const height = wall.height
      const width = 1000
      const thickness = 100
      const partitionID = getId('partition')
      const position = toVector3()

      const rotation = decomposeMatrix(wall.matrix).rotation
      const rot = radians(quatYToDeg(rotation))
      const x =
        wall.x -
        ((width + wall.thickness / 2) * Math.sin(rot) + (wall.thickness / 2) * Math.cos(rot))
      const z =
        wall.z +
        ((width / 2 + wall.thickness / 2) * Math.cos(rot) - (wall.thickness / 2) * Math.sin(rot))

      const newMatrix = new Matrix4().compose(
        toVector3([x, height / 2, z]),
        toQuaternion().setFromEuler(
          new Euler().setFromVector3(toVector3([0, getRightPerpendicular(-rot), 0])),
        ),
        toVector3([1, 1, 1]),
      )
      newMatrix.decompose(position, toQuaternion(), toVector3())
      function getRightPerpendicular(angle: number) {
        const perpendicularAngle = angle - Math.PI / 2
        return perpendicularAngle
      }
      store.items.set(
        partitionID,
        getBasePartition(
          {
            showAngle: false,
            visible: true,
            thickness: thickness,
            rotation: 0,
            x: x + (width / 2) * -Math.sin(rot),
            z: z + (thickness / 2) * Math.cos(rot),
            id: partitionID,
            height,
            width: width,
            matrix: newMatrix,
          },
          store,
        ),
      )
      store.layers[store.currentLayer].items.add(partitionID)
      store.currentObjectID = partitionID
      store.select(partitionID)
    })
  },
  projectPartitionOnWall: (partitionID: string, wallID: string, matrix: Matrix4) => {
    set((store) => {
      const wall = store.items.get(wallID)
      const partition = store.items.get(partitionID) as Partition
      const partitionWidth = partition.width
      if (!wall) return
      const { rotation, thickness } = wall
      const partitionPosition = toVector3()
      matrix.decompose(partitionPosition, toQuaternion(), toVector3())
      const end = toVector3(wall.end)
      const start = toVector3(wall.start)
      const wallDirection = end.clone().sub(start)
      const partitionToStart = partitionPosition.clone().sub(start)
      const projectedVector = partitionToStart.projectOnVector(wallDirection.clone().normalize())
      const adjustedPosition = start
        .clone()
        .add(wallDirection.normalize().multiplyScalar(projectedVector.length()))
      const finalX = adjustedPosition.x - (partitionWidth / 2) * Math.sin(rotation)
      const finalZ = adjustedPosition.z + (partitionWidth / 2) * Math.cos(rotation)
      const finalY =
        partitionPosition.y - partition.height / 2 < 0
          ? 0
          : partitionPosition.y - partition.height / 2
      function getRightPerpendicular(angle: number) {
        const perpendicularAngle = angle - Math.PI / 2
        return perpendicularAngle
      }

      if (!partition) return
      if (!partition.visible) partition.visible = true

      partition.matrix.makeRotationFromEuler(
        new Euler(0, getRightPerpendicular(-rotation), 0, 'XYZ'),
      )
      partition.matrix.setPosition(finalX, partitionPosition.y, finalZ)
      partition.x = finalX
      partition.y = finalY
      partition.z = finalZ
      partition.snappedWall = wallID
    })
  },
  editPartitionHeightFromFloor: (id: string, height: number) => {
    set((store) => {
      const item = store.items.get(id) as Item
      if (!item) return

      const pos = toVector3()
      item.matrix.decompose(pos, toQuaternion(), toVector3())
      item.matrix.setPosition(pos.x, height + item.height / 2, pos.z)
      item.y = height
    })
  },
  incrementPartitionZ: (id: string, z: number) => {
    const axis = 'z'
    set((store) => {
      const item = store.items.get(id) as Item
      if (!item) return

      const pos = toVector3()

      item.matrix = addItemOffset(item.matrix, z, axis)
      item.matrix.decompose(pos, toQuaternion(), toVector3())
      item.matrix.setPosition(pos.x, pos.y, pos.z)
      item.y = pos.y - item.height / 2
      item.x = pos.x
      item.z = pos.z
    })
  },
  incrementPartitionY: (id: string, y: number) => {
    const axis = 'y'
    set((store) => {
      const item = store.items.get(id) as Item
      if (!item) return

      const pos = toVector3()

      item.matrix = addItemOffset(item.matrix, y, axis)
      item.matrix.decompose(pos, toQuaternion(), toVector3())
      item.matrix.setPosition(pos.x, pos.y, pos.z)
      item.y = pos.y - item.height / 2
      item.x = pos.x
      item.z = pos.z
    })
  },
  incrementPartitionX: (id: string, x: number) => {
    const axis = 'x'
    set((store) => {
      const item = store.items.get(id) as Item
      if (!item) return

      const pos = toVector3()

      item.matrix = addItemOffset(item.matrix, x, axis)
      item.matrix.decompose(pos, toQuaternion(), toVector3())
      item.matrix.setPosition(pos.x, pos.y, pos.z)
      item.y = pos.y - item.height / 2
      item.x = pos.x
      item.z = pos.z
    })
  },
  updateStartEndPartition: (id: string) => {
    set((store) => {
      const partition = store.items.get(id) as Partition
      if (!partition) return
      const matrix = partition.matrix
      const { rotation: rot, position: pos } = decomposeMatrix(matrix)
      const quatY = radians(quatYToDeg(rot))
      const endX =
        pos.x + (partition.width * Math.cos(-quatY) - partition.thickness * Math.sin(-quatY)) / 2
      const endZ =
        pos.z + (partition.width * Math.sin(-quatY) + partition.thickness * Math.cos(-quatY)) / 2
      const startX =
        pos.x - (partition.width * Math.cos(-quatY) + partition.thickness * Math.sin(-quatY)) / 2
      const startZ =
        pos.z - (partition.width * Math.sin(-quatY) - partition.thickness * Math.cos(-quatY)) / 2

      const endX1 =
        pos.x - (partition.width * Math.cos(-quatY) - partition.thickness * Math.sin(-quatY)) / 2
      const endZ1 =
        pos.z - (partition.width * Math.sin(-quatY) + partition.thickness * Math.cos(-quatY)) / 2
      const startX1 =
        pos.x + (partition.width * Math.cos(-quatY) + partition.thickness * Math.sin(-quatY)) / 2
      const startZ1 =
        pos.z + (partition.width * Math.sin(-quatY) - partition.thickness * Math.cos(-quatY)) / 2

      partition.start = [startX, startZ]
      partition.end = [endX, endZ]
      partition.start1 = [startX1, startZ1]
      partition.end1 = [endX1, endZ1]
    })
  },
  projectItemOnPartition: (itemID: string, partitionID: string, itemMatrix: Matrix4) => {
    set((store) => {
      const items = store.items
      const partition = items.get(partitionID) as Partition
      const item = store.items.get(itemID) as Item
      const { matrix, start, end, start1, end1 } = partition
      const { rotation: quatRot } = decomposeMatrix(matrix)
      const { position: itemPosition } = decomposeMatrix(itemMatrix)
      const rotation = radians(quatYToDeg(quatRot))
      const start2 = toVector3(start)
      const end2 = toVector3(end)
      const startAlt = toVector3(start1)
      const endAlt = toVector3(end1)

      const wallDirection = end2.clone().sub(start2)
      const itemToStart = itemPosition.clone().sub(start2)
      const crossProduct = toVector3().crossVectors(wallDirection, itemToStart)

      let finalStart = toVector3()
      let finalEnd = toVector3()
      let lastDirection
      if (crossProduct.y < 0) {
        // Se acerca por la derecha
        finalStart = start2
        finalEnd = end2
        lastDirection = 'right'
      } else if (crossProduct.y > 0) {
        // Se acerca por la izquierda
        finalStart = startAlt
        finalEnd = endAlt
        lastDirection = 'left'
      } else {
        // Está alineado o moviéndose en la misma dirección
        if (lastDirection === 'right') {
          finalStart = start2
          finalEnd = end2
        } else if (lastDirection === 'left') {
          finalStart = startAlt
          finalEnd = endAlt
        }
      }

      const finalWallDirection = finalEnd.clone().sub(finalStart)
      const finalItemToStart = itemPosition.clone().sub(finalStart)
      const finalProjectedVector = finalItemToStart.projectOnVector(
        finalWallDirection.clone().normalize(),
      )
      const finalAlongWall = finalProjectedVector.length()
      const finalAdjustedPosition = finalStart
        .clone()
        .add(finalWallDirection.normalize().multiplyScalar(finalAlongWall))

      // Calcular el centro del item
      const itemCenter = toVector3([
        item.width / 2,
        item.height / 2 + itemPosition.y,
        item.thickness / 2,
      ])

      // Aplicar la rotación al centro del item
      itemCenter
        .applyQuaternion(toQuaternion().setFromEuler(new Euler(0, -rotation, 0, 'XYZ')))
        .add(finalAdjustedPosition)

      // Convertir la rotación del quaternion a una Matrix4 y extraer la Matrix3
      const neweuler = new Euler(0, -rotation, 0, 'XYZ')
      const rotationMatrix4 = new Matrix4().makeRotationFromEuler(neweuler)
      const rotationMatrix = new Matrix3().setFromMatrix4(rotationMatrix4)

      if (!item) return

      // Aplicar restricción de altura en modo 3D
      let restrictedY = itemPosition.y
      const mode = store.mode
      if (mode === 'MODE_3D_VIEW') {
        // Restringir mínimo a 0 siempre
        restrictedY = Math.max(0, restrictedY)

        // Si el item tiene altura, aplicar restricción máxima
        if (item.height) {
          // Usar la altura de la partición a la que está pegado
          const maxPartitionHeight = partition.height

          // Restringir máximo considerando la altura del mueble
          const maxYAllowed = maxPartitionHeight - item.height
          restrictedY = Math.min(restrictedY, maxYAllowed)
        }
      }

      let finalRot
      if (lastDirection === 'right') {
        finalRot = rotation
      } else {
        finalRot = rotation - Math.PI
      }
      item.matrix.makeRotationFromEuler(new Euler(0, finalRot, 0, 'XYZ'))

      if (!item.visible) item.visible = true
      item.matrix.setPosition(finalAdjustedPosition.x, restrictedY, finalAdjustedPosition.z)
      item.x = finalAdjustedPosition.x
      item.z = finalAdjustedPosition.z
      item.y = restrictedY
      // ItemGroups no deben tener snappedWall ni agregarse a partitions
      if (item.type !== 'itemGroup') {
        item.snappedWall = partitionID
      }
      if (item.tipoNormalizado === 'MAL') {
        partition.highItems.add(itemID)
      }
      if (item.tipoNormalizado === 'MBJ' || item.tipoNormalizado === 'COL') {
        partition.lowItems.add(itemID)
      }

      store.sceneBoundingBoxes.set(itemID, {
        boundingBox: new OBB(
          itemCenter,
          toVector3([item.width / 2, item.height / 2, item.thickness / 2]),
          rotationMatrix,
        ),
        type: 'items',
      })
    })
  },
  projectPartitionOnPartition: (itemID: string, partitionID: string, itemMatrix: Matrix4) => {
    set((store) => {
      if (!isPartition(partitionID) || !isPartition(itemID)) return

      const items = store.items,
        partition = items.get(partitionID) as Partition,
        item = store.items.get(itemID) as Partition,
        { matrix, start, end, start1, end1 } = partition,
        { rotation: quatRot } = decomposeMatrix(matrix),
        { position: itemPosition } = decomposeMatrix(itemMatrix),
        start2 = toVector3(start),
        end2 = toVector3(end),
        startAlt = toVector3(start1),
        endAlt = toVector3(end1),
        wallDirection = end2.clone().sub(start2),
        itemToStart = itemPosition.clone().sub(start2),
        rotation = radians(quatYToDeg(quatRot)),
        crossProduct = toVector3().crossVectors(wallDirection, itemToStart)

      function getRightPerpendicular(angle: number) {
        const perpendicularAngle = angle - Math.PI / 2
        return perpendicularAngle
      }

      let finalStart = toVector3()
      let finalEnd = toVector3()
      let lastDirection
      if (crossProduct.y < 0) {
        // Se acerca por la derecha
        finalStart = start2
        finalEnd = end2
        lastDirection = 'right'
      } else if (crossProduct.y > 0) {
        // Se acerca por la izquierda
        finalStart = startAlt
        finalEnd = endAlt
        lastDirection = 'left'
      } else {
        // Está alineado o moviéndose en la misma dirección
        if (lastDirection === 'right') {
          finalStart = start2
          finalEnd = end2
        } else if (lastDirection === 'left') {
          finalStart = startAlt
          finalEnd = endAlt
        }
      }

      let finalRot
      if (lastDirection === 'right') {
        finalRot = getRightPerpendicular(rotation)
      } else {
        finalRot = getRightPerpendicular(rotation) - Math.PI
      }

      const finalY =
          itemPosition.y - partition.height / 2 < 0 ? 0 : itemPosition.y - partition.height / 2,
        finalWallDirection = finalEnd.clone().sub(finalStart),
        finalItemToStart = itemPosition.clone().sub(finalStart),
        finalProjectedVector = finalItemToStart.projectOnVector(
          finalWallDirection.clone().normalize(),
        ),
        finalAlongWall = finalProjectedVector.length(),
        finalAdjustedPosition = finalStart
          .clone()
          .add(finalWallDirection.normalize().multiplyScalar(finalAlongWall))

      let finalX
      let finalZ
      if (lastDirection === 'right') {
        finalX = finalAdjustedPosition.x - (item.width / 2) * Math.sin(-rotation)
        finalZ = finalAdjustedPosition.z + (item.width / 2) * Math.cos(-rotation)
      } else {
        finalX = finalAdjustedPosition.x + (item.width / 2) * Math.sin(-rotation)
        finalZ = finalAdjustedPosition.z - (item.width / 2) * Math.cos(-rotation)
      }
      if (!item) return

      item.matrix.makeRotationFromEuler(new Euler(0, finalRot, 0, 'XYZ'))
      if (!item.visible) item.visible = true
      item.matrix.setPosition(finalX, itemPosition.y, finalZ)
      item.x = finalX
      item.z = finalZ
      item.y = finalY
      // ItemGroups no deben tener snappedWall
      if (item.type !== 'itemGroup') {
        item.snappedWall = partitionID
      }
    })
  },
})
